import type { Command } from '@o2d/history';
import type { BoardState } from '../state/board-state.js';
import type { BoardObject, Viewport } from '../interfaces/board-object.interface.js';
import { scaleObjectBy } from './normalise-zoom.helper.js';

/**
 * Anke, 2026-07-16 — "Auf 100 % normalisieren": Jedes Objekt um die aktuelle
 * Zoomstufe vergroessern und die Stufe auf 1 setzen. Das Bild bleibt gleich,
 * die Anzeige sagt danach 100 % (Rechnung siehe normalise-zoom.helper.ts).
 *
 * WARUM DIESER BEFEHL DIE ANSICHT MITSCHREIBT — die einzige Ausnahme im Paket:
 * Verschieben und Zoomen sind bewusst NICHT im Verlauf (siehe
 * viewport.controller.ts) — sie aendern nichts am Dokument. Hier ist es anders:
 * Der Eingriff besteht aus zwei Haelften, die nur ZUSAMMEN das Bild
 * unveraendert lassen. Naehme man beim Rueckgaengigmachen nur die Objekte
 * zurueck und liesse die Stufe auf 100 %, schrumpfte der Inhalt schlagartig auf
 * 1/s — ein Rueckgaengig, das etwas anderes tut als die Umkehrung. Deshalb
 * gehoert die Ansicht hier in den Befehl.
 *
 * Die Buehne folgt dabei von selbst: Das Board-Element zieht sie bei jedem
 * `viewport-changed` nach.
 */
export class NormaliseZoomCommand implements Command<BoardState> {
  readonly label = 'Auf 100 % normalisieren';

  /** Objekte vor dem Eingriff — die Umkehrung setzt sie zurueck, statt mit
   *  1/k zurueckzurechnen: Zwei Multiplikationen hintereinander treffen wegen
   *  der Gleitkomma-Rechnung nicht sicher wieder den Ausgangswert. */
  readonly #before: ReadonlyArray<BoardObject>;
  readonly #after: ReadonlyArray<BoardObject>;
  readonly #viewportBefore: Viewport;
  readonly #viewportAfter: Viewport;

  private constructor(
    before: ReadonlyArray<BoardObject>,
    after: ReadonlyArray<BoardObject>,
    viewportBefore: Viewport,
    viewportAfter: Viewport,
  ) {
    this.#before = before;
    this.#after = after;
    this.#viewportBefore = viewportBefore;
    this.#viewportAfter = viewportAfter;
  }

  /**
   * Baut den Befehl aus dem aktuellen Zustand — oder `null`, wenn es nichts zu
   * tun gibt (Stufe steht schon auf 100 %). `null` heisst: nichts in den
   * Verlauf legen, sonst gaebe es einen Rueckgaengig-Schritt, der nichts tut.
   */
  static from(state: BoardState): NormaliseZoomCommand | null {
    const viewport = state.viewport;
    const k = viewport.scale;
    if (k === 1) return null;

    const before = state.allObjects().map((o) => ({ ...o }) as BoardObject);
    const after = before.map((o) => scaleObjectBy(o, k));

    return new NormaliseZoomCommand(
      before,
      after,
      { ...viewport },
      // Verschiebung BLEIBT — sie ist in Bildschirmpixeln und gilt unveraendert
      // weiter, weil um den Welt-Ursprung skaliert wurde.
      { x: viewport.x, y: viewport.y, scale: 1 },
    );
  }

  apply(state: BoardState): void {
    this.#write(state, this.#after, this.#viewportAfter);
  }

  revert(state: BoardState): void {
    this.#write(state, this.#before, this.#viewportBefore);
  }

  #write(state: BoardState, objects: ReadonlyArray<BoardObject>, viewport: Viewport): void {
    for (const obj of objects) {
      // Einzeln ersetzen statt replaceAll(): replaceAll wirft die Konva-Knoten
      // weg und baut sie neu auf — das loescht die Auswahl und laesst das Board
      // sichtbar zucken. object-updated laesst die Knoten leben.
      state.replaceObject(obj.id, { ...obj });
    }
    state.setViewport({ ...viewport });
  }
}
